import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const sections = [
    {
        id: "acceptance",
        title: "1. Acceptance of Terms",
        body: [
            "By accessing or using PipeGuide (the \"Service\"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.",
            "PipeGuide is operated from the Republic of South Africa. These terms are governed by South African law.",
        ],
    },
    {
        id: "accounts",
        title: "2. Accounts and Eligibility",
        body: [
            "You must be at least 18 years old and authorised to act on behalf of your organisation to create a paid account.",
            "You are responsible for keeping your login credentials secure. You may sign in via email + password or Google SSO.",
        ],
    },
    {
        id: "subscriptions",
        title: "3. Subscriptions, Billing and Cancellation",
        body: [
            "Paid plans (Pro, Business) are billed month-to-month. All fees are quoted in South African Rand (ZAR) and are exclusive of VAT. International prices shown in USD are for reference — Paystack processes the actual charge in ZAR at a conversion rate of approximately R18 = $1 (adjusted periodically).",
            "You are charged on the same day each month for the plan you selected. Plan changes (upgrades, downgrades, extra seats) are pro-rated for the remainder of the current billing month.",
            "You may cancel your paid plan at any time by sending a written cancellation request to support@pipeguide.tech. Your plan remains active for 30 days from the date of the request, after which your organisation is downgraded to the free Starter tier. There are no cancellation fees, termination fees, or minimum commitment periods.",
            "Optional one-time services — such as Training & Setup and Call Packs — are non-refundable once delivery has commenced but may be refunded in full if cancelled before any delivery activity has begun.",
        ],
    },
    {
        id: "acceptable-use",
        title: "4. Acceptable Use",
        body: [
            "You agree not to use the Service to: (a) record calls in jurisdictions where doing so without consent is unlawful; (b) attempt to reverse engineer the platform; (c) resell or sublicense access without our written consent.",
            "You are responsible for ensuring all call participants are notified that the conversation is being analysed by AI in accordance with applicable laws (e.g. POPIA, GDPR).",
        ],
    },
    {
        id: "data",
        title: "5. Customer Data",
        body: [
            "Customer Data — including transcripts, scores and CRM exports — remains your property. We process it solely to provide the Service.",
            "We do not share Customer Data between organisations. See our Privacy Policy and Data Processing Addendum for details.",
        ],
    },
    {
        id: "termination",
        title: "6. Termination",
        body: [
            "We may suspend or terminate your access if you materially breach these Terms or fail to pay outstanding invoices after written notice.",
            "Upon termination, your data will be retained for 30 days in case of reactivation, after which it will be deleted from production systems.",
        ],
    },
    {
        id: "liability",
        title: "7. Limitation of Liability",
        body: [
            "To the maximum extent permitted by law, PipeGuide's total liability for any claim arising out of these Terms is limited to the fees paid by you in the 12 months preceding the claim.",
            "We are not liable for indirect, consequential or special damages, including lost profits or lost opportunities.",
        ],
    },
    {
        id: "changes",
        title: "8. Changes to these Terms",
        body: [
            "We may update these Terms from time to time. Material changes will be announced via email at least 30 days before they take effect.",
        ],
    },
    {
        id: "contact",
        title: "9. Contact",
        body: [
            "Questions about these Terms? Email support@support.pipeguide.tech.",
        ],
    },
];

export default function Terms() {
    return (
        <div data-testid="terms-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            <section className="bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Legal</p>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">Terms of Service</h1>
                    <p className="mt-4 text-zinc-600">Last updated: 28 April 2026</p>
                </div>
            </section>

            <section className="bg-white flex-1">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-3">
                        <div className="sticky top-24">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">On this page</p>
                            <nav className="space-y-2">
                                {sections.map((s) => (
                                    <a key={s.id} href={`#${s.id}`} data-testid={`terms-nav-${s.id}`} className="block text-sm text-zinc-600 hover:text-brand-primary">
                                        {s.title}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    <article className="lg:col-span-9 prose prose-zinc max-w-none">
                        {sections.map((s) => (
                            <div key={s.id} id={s.id} data-testid={`terms-section-${s.id}`} className="mb-10 scroll-mt-24">
                                <h2 className="font-display text-2xl font-bold text-zinc-900">{s.title}</h2>
                                {s.body.map((p, i) => (
                                    <p key={i} className="mt-4 text-zinc-700 leading-relaxed">{p}</p>
                                ))}
                            </div>
                        ))}
                    </article>
                </div>
            </section>

            <Footer />
        </div>
    );
}
